/**
 * Tenant Admin — MCP 연동 요약 (Phase 1)
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle } from '@shared/ui/primitives';
import { useAuthStore } from '@/stores/authStore';
import { getTenantAuthStorage } from '@/utils/authStorage';
import { config } from '@shared/config';
import './tenant-admin.css';

type McpConnectionInfo = {
  tenantSlug: string;
  url: string;
  transport: string;
  status: string;
  sync?: { promptCount: number; enabledToolCount: number; lastSyncAt: string };
};

async function fetchMcpConnection(tenantId: string, token: string): Promise<McpConnectionInfo> {
  const base = config.apiUrl ? `${config.apiUrl}/api/aiops` : '/api/aiops';
  const res = await fetch(`${base}/mcp/connection`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Tenant-Id': tenantId,
    },
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const j = JSON.parse(text) as { message?: string; error?: string };
      msg = j.message || j.error || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return JSON.parse(text) as McpConnectionInfo;
}

export const TenantMcpPage: React.FC = () => {
  const { user } = useAuthStore();
  const [info, setInfo] = useState<McpConnectionInfo | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.userType !== 'tenant-user' || !user.tenantId) return;
    const token = getTenantAuthStorage().getToken();
    if (!token) return;
    fetchMcpConnection(user.tenantId, token)
      .then(setInfo)
      .catch((e) => setError(e instanceof Error ? e.message : 'load failed'));
  }, [user]);

  const aiopsAdminUrl = `${config.portalUrl || ''}/aiops-admin/#/mcp-connection`;

  return (
    <div className="tenant-admin-page">
      <h1 className="tenant-admin-title">MCP 연동</h1>
      <p className="tenant-admin-desc">Tenant MCP Gateway 연결 상태 및 클라이언트 설정 안내</p>

      {error && (
        <Alert variant="error" title="오류" description={error} className="mb-4" />
      )}

      {info && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">연결 정보</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>상태: {info.status}</div>
              <div>URL: <code className="text-xs">{info.url}</code></div>
              <div>Transport: {info.transport}</div>
              {info.sync && (
                <div className="text-muted-foreground">
                  Prompts {info.sync.promptCount} · Tools {info.sync.enabledToolCount} enabled
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">빠른 링크</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button variant="outline" asChild>
                <a href={aiopsAdminUrl} target="_blank" rel="noreferrer">
                  AIOps Admin — MCP 연결 설정
                </a>
              </Button>
              <Button variant="outline" asChild>
                <a href={`${config.portalUrl || ''}/aiops-admin/#/mcp-tools`} target="_blank" rel="noreferrer">
                  AIOps Admin — Tool 활성화
                </a>
              </Button>
              <Button variant="outline" asChild>
                <a href="/tenant-admin/access-control">접근 제어 — MCP 권한</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
